import { Component, Inject, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { MailService } from 'src/app/_services/dispo/mail.service';
import { BaseTextPresetService } from 'src/app/_services/dispo/base-text-preset.service';
import { OpenAIService } from 'src/app/_services/dispo/openai.service';
import { ContactPickerComponent } from '../contact_picker/contact_picker.component';
import { EmailPresetPickerDialogComponent } from './email-preset-picker-dialog.component';

export interface EmailDialogData {
  subject?: string;
  body?: string;
  recipients?: string[];
  attachments?: { name: string; url?: string }[];
  /** Kontext (z. B. orderId, mailTypeId, appointmentId für Termin-Bestätigungslinks) */
  context?: { orderId?: number; mailTypeId?: number; appointmentId?: number };
}

@Component({
  selector: 'app-email-dialog',
  templateUrl: './email-dialog.component.html',
  styleUrls: ['./email-dialog.component.css']
})
export class EmailDialogComponent implements OnInit, AfterViewInit {
  emailForm: FormGroup;
  submitted = false;
  loading = false;
  generating = false;
  presetList: any[] = [];
  mailTypes: { id: number; name: string; code?: string }[] = [];

  promptText = '';

  @ViewChild('bodyEditor') bodyEditor!: ElementRef<HTMLDivElement>;

  // Empfänger als „Chips“
  emailTags: string[] = [];
  newEmailInput = '';

  constructor(
    public dialogRef: MatDialogRef<EmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EmailDialogData,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private mailService: MailService,
    private presetService: BaseTextPresetService,
    private openAIService: OpenAIService,
    private dialog: MatDialog
  ) {
    const initialRecipients = (data.recipients && data.recipients.length) ? data.recipients.join(', ') : '';
    if (data.recipients && data.recipients.length) {
      this.emailTags = [...data.recipients];
    }

    const defaultMailTypeId = data.context?.mailTypeId ?? null;
    this.emailForm = this.fb.group({
      recipients: [initialRecipients, [Validators.required, this.multipleEmailValidator]],
      cc: [''],
      mailTypeId: [defaultMailTypeId],
      subject: [data.subject || '', Validators.required],
      body: [data.body || '', Validators.required]
    });
  }

  get f(): { [key: string]: AbstractControl } {
    return this.emailForm.controls;
  }

  ngOnInit(): void {
    this.fetchPresets();
    this.mailService.getMailTypes().subscribe({
      next: (list) => { this.mailTypes = list || []; },
      error: () => { this.mailTypes = []; }
    });
  }

  ngAfterViewInit(): void {
    this.syncEditorFromControl();
  }

  // ---------- Kontakte / Empfänger ----------

  openContactPicker(): void {
    const dialogRef = this.dialog.open(ContactPickerComponent, {
      width: '80%',
      height: '85vh',
      maxWidth: '1200px',
      maxHeight: '90vh'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && Array.isArray(result)) {
        result.forEach(contact => {
          const email = (contact.email || '').trim();
          if (email && !this.emailTags.includes(email)) {
            this.emailTags.push(email);
          }
        });
        this.updateRecipientsFormControl();
      } else if (result?.email) {
        const email = result.email.trim();
        if (email && !this.emailTags.includes(email)) {
          this.emailTags.push(email);
          this.updateRecipientsFormControl();
        }
      }
    });
  }

  onEmailInput(event: any): void {
    const value = event.target.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(value.trim())) {
      setTimeout(() => {
        if (this.newEmailInput === value && emailRegex.test(value.trim())) {
          this.addEmailTag();
        }
      }, 800);
    }
  }

  addEmailTag(): void {
    this.flushRecipientInput();
  }

  /** Übernimmt manuell eingegebene Empfänger (auch mehrere, getrennt durch , oder ;) in die Tags/FormControl. */
  flushRecipientInput(): void {
    const raw = (this.newEmailInput || '').trim();
    if (!raw) return;

    const parts = raw
      .split(/[,;\s]+/g)
      .map(p => p.trim())
      .filter(Boolean);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let added = false;
    parts.forEach(email => {
      if (emailRegex.test(email) && !this.emailTags.includes(email)) {
        this.emailTags.push(email);
        added = true;
      }
    });

    if (added) {
      this.newEmailInput = '';
      this.updateRecipientsFormControl();
    }
  }

  removeEmailTag(index: number): void {
    this.emailTags.splice(index, 1);
    this.updateRecipientsFormControl();
  }

  updateRecipientsFormControl(): void {
    const recipientsValue = this.emailTags.join(', ');
    this.emailForm.get('recipients')?.setValue(recipientsValue);
  }

  /** Wert für Validierung: Tags + aktuell getippte Eingabe (damit manuell eingegebene Adressen sofort zählen). */
  getEffectiveRecipientsValue(): string {
    const fromTags = this.emailTags.join(', ');
    const raw = (this.newEmailInput || '').trim();
    if (!raw) return fromTags;
    return fromTags ? fromTags + ', ' + raw : raw;
  }

  /** Stellt sicher, dass das recipients-FormControl den aktuellen Stand (inkl. getippter Text) hat – für Validierung/Fehleranzeige. */
  ensureRecipientsControlSynced(): void {
    const effective = this.getEffectiveRecipientsValue();
    const ctrl = this.emailForm.get('recipients');
    if (ctrl && ctrl.value !== effective) {
      ctrl.setValue(effective);
      ctrl.markAsTouched();
    }
  }

  /** Gibt true zurück, wenn Empfänger ungültig sind – nach Sync der aktuellen Eingabe. */
  get recipientsInvalid(): boolean {
    this.ensureRecipientsControlSynced();
    return !!this.emailForm.get('recipients')?.invalid;
  }

  multipleEmailValidator(control: FormControl) {
    const val = control.value;
    if (val === null || val === undefined || val === '') return null;
    const emails = String(val).split(/[,;\s]+/g).map((email: string) => email.trim()).filter(Boolean);
    if (emails.length === 0) return { invalidEmail: true };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        return { invalidEmail: true };
      }
    }
    return null;
  }

  // ---------- Presets ----------

  fetchPresets(): void {
    this.presetService.getAll().subscribe(
      data => {
        this.presetList = data || [];
      },
      () => {
        this.presetList = [];
      }
    );
  }

  applyPreset(item: any): void {
    const bodyCtrl = this.emailForm.get('body');
    if (!bodyCtrl) { return; }
    const current: string = bodyCtrl.value || '';
    const rawText: string = item?.text || '';
    const textHtml = rawText.replace(/\n/g, '<br>');
    const appended = current ? current + '<br><br>' + textHtml : textHtml;
    bodyCtrl.setValue(appended);
    this.syncEditorFromControl();
  }

  openPresetPicker(): void {
    if (!this.presetList || !this.presetList.length) {
      this.toastr.warning('Keine Presets verfügbar.', 'Presets');
      return;
    }
    const dialogRef = this.dialog.open(EmailPresetPickerDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        presets: this.presetList
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.applyPreset(result);
        this.toastr.info(`Preset "${result.name}" eingefügt.`, 'Presets');
      }
    });
  }

  // ---------- OpenAI Prompt ----------

  generateFromPrompt(): void {
    const prompt = (this.promptText || '').trim();
    if (!prompt) {
      this.toastr.warning('Bitte zuerst einen Prompt eingeben.', 'KI-Text');
      return;
    }
    this.generating = true;
    this.openAIService.getResponse(prompt).subscribe({
      next: (text: string) => {
        const bodyCtrl = this.emailForm.get('body');
        const current: string = (bodyCtrl?.value as string) || '';
        const textHtml = (text || '').replace(/\n/g, '<br>');
        const appended = current ? current + '<br><br>' + textHtml : textHtml;
        bodyCtrl?.setValue(appended);
        this.syncEditorFromControl();
        this.generating = false;
      },
      error: () => {
        this.toastr.error('KI-Text konnte nicht generiert werden.', 'KI-Text');
        this.generating = false;
      }
    });
  }

  // ---------- Speichern / Senden ----------

  submit(): void {
    // Für Validierung immer den aktuellen Input mit berücksichtigen
    this.ensureRecipientsControlSynced();
    this.submitted = true;
    if (this.emailForm.invalid) {
      this.toastr.warning('Bitte Empfänger, Betreff und Text prüfen.', 'E-Mail');
      return;
    }

    // Danach (für saubere Tags/Anzeige) übernehmen wir den Input in die Tag-Liste, falls möglich
    this.flushRecipientInput();
    const value = this.emailForm.value;
    const attachments = this.data.attachments || [];
    const context = this.data.context || null;

    // Payload im Stil des bestehenden Mail-Moduls (MailController::store)
    const mailPayload: any = {
      title: value.subject,
      recipients: value.recipients,
      text: value.body,
      documents: JSON.stringify(attachments),
      order_id: context && context.orderId ? context.orderId : null,
      mail_type_id: value.mailTypeId || null,
      appointment_id: context && context.appointmentId ? context.appointmentId : null
    };

    this.loading = true;
    this.mailService.store(mailPayload).subscribe(
      () => {
        this.loading = false;
        this.toastr.success('E-Mail wurde gespeichert und versendet.', 'E-Mail');
        this.dialogRef.close(true);
      },
      (err) => {
        this.loading = false;
        this.toastr.error(err.error?.message || 'Fehler beim Senden der E-Mail', 'E-Mail');
      }
    );
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  // ---------- Einfacher HTML-Editor ----------

  onEditorInput(): void {
    if (!this.bodyEditor) { return; }
    const html = this.bodyEditor.nativeElement.innerHTML || '';
    this.emailForm.get('body')?.setValue(html);
  }

  execCmd(command: string): void {
    document.execCommand(command, false, undefined);
    this.onEditorInput();
  }

  private syncEditorFromControl(): void {
    if (!this.bodyEditor) { return; }
    const value: string = this.emailForm.get('body')?.value || '';
    this.bodyEditor.nativeElement.innerHTML = value;
  }
}

